import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { apiClient } from '../../config';

const Leads = () => {
  const PAGE_SIZE = 10;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [showQuickView, setShowQuickView] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredLeads = useMemo(() => {
    return leads
      .filter(lead => {
        const searchLower = searchTerm.toLowerCase();
        return (
          lead.fullName.toLowerCase().includes(searchLower) ||
          lead.email.toLowerCase().includes(searchLower) ||
          lead.phone.includes(searchTerm)
        );
      })
      .filter(lead => {
        if (statusFilter === 'All') return true;
        return lead.status === statusFilter;
      })
      .filter(lead => {
        const { startDate, endDate } = dateRange;
        if (!startDate || !endDate) return true;
        const leadDate = new Date(lead.createdAt);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return leadDate >= start && leadDate <= end;
      });
  }, [leads, searchTerm, statusFilter, dateRange]);

  const handleDateChange = (e) => {
    setDateRange({ ...dateRange, [e.target.name]: e.target.value });
  };

  const handleOpenQuickView = (lead) => {
    setSelectedLead(lead);
    setShowQuickView(true);
  };

  const handleCloseQuickView = () => {
    setShowQuickView(false);
    setSelectedLead(null);
  };

  const statusOptions = ["All", "New", "Contacted", "Qualified", "Lost"];

  useEffect(() => {
    const mapContactToLead = (c) => ({
      _id: c._id,
      fullName: c.name || "-",
      email: c.email || "-",
      phone: c.phone || "-",
      country: c.country || "-",
      location: { state: "-", city: "-", address: "-" },
      status: c.status || "New",
      priority: c.priority || "Medium",
      source: c.type || "contact",
      createdAt: c.createdAt || new Date().toISOString(),
      updatedAt: c.updatedAt || new Date().toISOString(),
      followUps: Array.isArray(c.followUps) ? c.followUps : [],
    });

    const fetchLeads = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await apiClient.get('/api/contact');
        const payload = response.data;
        const contacts = Array.isArray(payload?.contacts) ? payload.contacts : [];
        setLeads(contacts.map(mapContactToLead));
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to fetch leads");
        setLeads([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, []);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / PAGE_SIZE));
  const paginatedLeads = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredLeads.slice(start, start + PAGE_SIZE);
  }, [filteredLeads, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-semibold mb-0">Manage Leads</h4>
        <Link to="/add-lead" className="btn btn-primary">
          + Add New Lead
        </Link>
      </div>

        {/* Filters */}
        <div className="card mb-4 border-0">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label htmlFor="search" className="form-label fw-semibold">Search</label>
                <input
                  type="text"
                  id="search"
                  className="form-control"
                  placeholder="Search by name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label htmlFor="statusFilter" className="form-label fw-semibold">Status</label>
                <select
                  id="statusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label htmlFor="startDate" className="form-label fw-semibold">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  className="form-control"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="endDate" className="form-label fw-semibold">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  className="form-control"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="card border-0">
          <div className="card-body p-0">
            {loading && (
              <div className="p-4 text-center text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Loading leads...
              </div>
            )}
            {!loading && error && <div className="alert alert-danger m-4">{error}</div>}
            
            {!loading && (
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th scope="col" className="ps-4" style={{ width: "60px" }}>#</th>
                      <th>Full Name</th>
                      <th>Email & Phone</th>
                      <th>Created On</th>
                      <th>Status</th>
                      <th className="pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLeads.length > 0 ? paginatedLeads.map((lead, index) => (
                      <tr key={lead._id}>
                        <th scope="row" className="ps-4">{(currentPage - 1) * PAGE_SIZE + index + 1}</th>
                        <td className="fw-semibold">{lead.fullName}</td>
                        <td>
                          <div>{lead.email}</div>
                          <div className="text-muted" style={{ fontSize: '0.85rem' }}>{lead.phone}</div>
                        </td>
                        <td>{new Date(lead.createdAt).toLocaleDateString()}</td>
                        <td>
                          <span className={`badge bg-${lead.status === 'New' ? 'success' : lead.status === 'Qualified' ? 'info' : lead.status === 'Lost' ? 'danger' : 'warning'}`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="pe-4 text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleOpenQuickView(lead)}
                            >
                              Quick View
                            </button>
                            <Link to={`/lead/${lead._id}`} className="btn btn-primary btn-sm px-2">
                              <Eye size={16} />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">No leads found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {filteredLeads.length > 0 && !loading && (
              <div className="d-flex align-items-center justify-content-between p-4 border-top">
                <small className="text-muted">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, filteredLeads.length)} of {filteredLeads.length}
                </small>
                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    style={{ minWidth: '88px' }}
                  >
                    Previous
                  </button>
                  {getPageNumbers().map((page, idx) => (
                    page === "..." ? (
                      <span key={`dots-${idx}`} className="text-muted px-1">...</span>
                    ) : (
                      <button
                        key={`page-${page}`}
                        type="button"
                        className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setCurrentPage(page)}
                        style={{ minWidth: '34px' }}
                      >
                        {page}
                      </button>
                    )
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    style={{ minWidth: '70px' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      {showQuickView && selectedLead && (
        <div className="modal show fade d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '14px' }}>
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold">Lead Quick View</h5>
                <button type="button" className="btn-close" onClick={handleCloseQuickView}></button>
              </div>
              <div className="modal-body py-4">
                <div className="mb-3">
                  <span className="text-muted d-block small">Full Name</span>
                  <span className="fw-semibold text-dark fs-5">{selectedLead.fullName}</span>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <span className="text-muted d-block small">Email Address</span>
                    <span className="text-dark">{selectedLead.email}</span>
                  </div>
                  <div className="col-6">
                    <span className="text-muted d-block small">Phone Number</span>
                    <span className="text-dark">{selectedLead.phone}</span>
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <span className="text-muted d-block small">Country</span>
                    <span className="text-dark">{selectedLead.country}</span>
                  </div>
                  <div className="col-6">
                    <span className="text-muted d-block small">Status</span>
                    <span className={`badge bg-${selectedLead.status === 'New' ? 'success' : selectedLead.status === 'Qualified' ? 'info' : selectedLead.status === 'Lost' ? 'danger' : 'warning'}`}>
                      {selectedLead.status}
                    </span>
                  </div>
                </div>
                <div className="row g-3">
                  <div className="col-6">
                    <span className="text-muted d-block small">Priority</span>
                    <span className="text-dark">{selectedLead.priority}</span>
                  </div>
                  <div className="col-6">
                    <span className="text-muted d-block small">Created On</span>
                    <span className="text-dark">{new Date(selectedLead.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top-0 pt-0">
                <button type="button" className="btn btn-light" onClick={handleCloseQuickView}>Close</button>
                <Link to={`/lead/${selectedLead._id}`} className="btn btn-primary" onClick={handleCloseQuickView}>
                  Open Full View
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Leads;
